/**
 * useLivePositions — overlays sub-second WebSocket price ticks onto the static
 * position objects that come from the on-chain SWR fetch.
 *
 * Every tick recalculates:
 *   • markPrice        (live bid/ask)
 *   • pendingDelta     (unrealised P&L in 1e30 USD)
 *   • hasProfit
 *   • deltaStr / deltaPercentageStr  (formatted for display)
 *
 * Everything else (size, collateral, entry price, orders) comes straight from
 * the original position — no on-chain calls are added.
 */
import { useMemo } from "react";
import { useLivePrices } from "lib/indexer/useLivePrices";
import { USD_DECIMALS } from "config/factors";
import { getNormalizedTokenSymbol } from "config/tokens";
import { getDeltaStr } from "lib/legacy";
import { bigMath } from "lib/bigmath";

const USD_30 = 10n ** 30n;

function usdToP30(usd: number): bigint {
  return BigInt(Math.round(usd * 1e8)) * (10n ** 22n);
}

export function useLivePositions<T extends {
  indexToken: { symbol: string; isWrapped?: boolean; isNative?: boolean };
  collateralToken: { symbol: string };
  isLong: boolean;
  size: bigint;
  collateral: bigint;
  averagePrice: bigint;
  markPrice?: bigint;
  pendingDelta?: bigint;
  hasProfit?: boolean;
  deltaStr?: string;
  deltaPercentageStr?: string;
  leverage?: bigint;
}>(positions: T[]): T[] {
  const prices = useLivePrices();

  return useMemo(() => {
    if (!positions || positions.length === 0) return positions;

    return positions.map((pos) => {
      const normSymbol = getNormalizedTokenSymbol(pos.indexToken.symbol);
      const liveUsd = prices[normSymbol.toUpperCase()];

      if (!liveUsd || liveUsd <= 0) return pos;

      const livePrice = usdToP30(liveUsd);

      // P&L: delta = (markPrice - avgPrice) / avgPrice * size (for long)
      //             = (avgPrice - markPrice) / avgPrice * size (for short)
      let pendingDelta = 0n;
      let hasProfit = false;

      if (pos.averagePrice > 0n && pos.size > 0n) {
        if (pos.isLong) {
          if (livePrice >= pos.averagePrice) {
            pendingDelta = (pos.size * (livePrice - pos.averagePrice)) / pos.averagePrice;
            hasProfit = true;
          } else {
            pendingDelta = (pos.size * (pos.averagePrice - livePrice)) / pos.averagePrice;
            hasProfit = false;
          }
        } else {
          if (pos.averagePrice >= livePrice) {
            pendingDelta = (pos.size * (pos.averagePrice - livePrice)) / pos.averagePrice;
            hasProfit = true;
          } else {
            pendingDelta = (pos.size * (livePrice - pos.averagePrice)) / pos.averagePrice;
            hasProfit = false;
          }
        }
      }

      const deltaPercentage = pos.size > 0n
        ? (pendingDelta * 10000n) / pos.size
        : 0n;

      const { deltaStr, deltaPercentageStr } = getDeltaStr({
        delta: pendingDelta,
        deltaPercentage,
        hasProfit,
      });

      return {
        ...pos,
        markPrice: livePrice,
        pendingDelta,
        hasProfit,
        deltaStr,
        deltaPercentageStr,
        // Mirror after-fees fields from base delta (fees already deducted on-chain)
        pendingDeltaAfterFees: pendingDelta,
        hasProfitAfterFees: hasProfit,
      };
    });
  }, [positions, prices]);
}
